import { cn } from "@/lib/utils";
import { Loader2 } from "lucide-react";
import {
  ButtonHTMLAttributes,
  CSSProperties,
  ReactNode,
  forwardRef,
} from "react";

export type OutlineButtonVariant =
  | "gold"
  | "blue"
  | "neutral"
  | "calendar"
  | "success";

type ColoredOutlineButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  children: ReactNode;
  wrapperClassName?: string;
  innerClassName?: string;
  size?: "icon" | "sm" | "default";
  isLoading?: boolean;
  variant?: OutlineButtonVariant;
  /** CSS color for the border (overrides variant when set). */
  borderColor?: string;
  /** CSS color for icon and text (overrides variant when set). */
  contentColor?: string;
  /** Subtle inner fill tinted from the border hue. Defaults to true for blue/success. */
  tintedBackground?: boolean;
};

const sizeClasses = {
  icon: "h-10 w-10 p-0",
  sm: "h-8 px-2.5 py-1.5 text-xs font-medium gap-1.5",
  default: "h-10 px-4 py-2 text-sm font-medium gap-2",
};

const variantPreset: Record<
  OutlineButtonVariant,
  {
    inner: string;
    innerBorder: string;
    innerBg: string;
    innerBgTinted: string;
    hover: string;
  }
> = {
  /** Flat gold outline — same shape as blue (no gradient ring). */
  gold: {
    inner: "text-[#1c1508] dark:text-[#1c1508]",
    innerBorder: "border border-[#d4a017] dark:border-[#f0c84a]",
    innerBg: "bg-background",
    innerBgTinted: "bg-[#d5a021] dark:bg-[#d5a021]/95",
    hover:
      "hover:bg-[#c4920f] hover:border-[#d4a017] dark:hover:bg-[#c9a227] dark:hover:border-[#f0c84a]",
  },
  blue: {
    inner: "text-blue-700 dark:text-blue-300",
    innerBorder: "border border-blue-500/30 dark:border-blue-400/35",
    innerBg: "bg-background",
    innerBgTinted: "bg-blue-500/[0.1] dark:bg-blue-500/[0.1]",
    hover:
      "hover:bg-blue-500/[0.18] hover:border-blue-500 dark:hover:border-blue-400/45 dark:hover:bg-blue-500/[0.22] hover:text-blue-800 dark:hover:text-blue-200",
  },
  neutral: {
    inner: "text-foreground",
    innerBorder: "border border-foreground/20 dark:border-foreground/25",
    innerBg: "bg-background",
    innerBgTinted: "bg-foreground/[0.04]",
    hover:
      "hover:bg-foreground/[0.06] hover:border-foreground/35 dark:hover:border-foreground/40",
  },
  calendar: {
    inner: "text-[#DC3C3C] dark:text-[#E5484D]",
    innerBorder: "border border-foreground/20 dark:border-foreground/25",
    innerBg: "bg-background",
    innerBgTinted: "bg-background",
    hover: "hover:border-foreground/45 dark:hover:border-foreground/50",
  },
  /** Emerald outline confirm actions (Arkivera valda, etc.) */
  success: {
    inner: "text-emerald-800 dark:text-emerald-300",
    innerBorder: "border border-emerald-600/35 dark:border-emerald-400/40",
    innerBg: "bg-background",
    innerBgTinted: "bg-emerald-500/[0.1] dark:bg-emerald-500/[0.12]",
    hover:
      "hover:bg-emerald-500/[0.18] hover:border-emerald-600 dark:hover:bg-emerald-500/[0.22] dark:hover:border-emerald-400/55",
  },
};

/** Icon size for square icon-only outline buttons (h-10 w-10). */
export const OUTLINE_ICON_BUTTON_ICON_CLASS = "h-[18px] w-[18px]";

/** Icons beside label text — slightly smaller than text-xs for balance. */
export const OUTLINE_TEXT_BUTTON_ICON_CLASS = "h-3.5 w-3.5 shrink-0";

export const ColoredOutlineButton = forwardRef<
  HTMLButtonElement,
  ColoredOutlineButtonProps
>(function ColoredOutlineButton(
  {
    children,
    className,
    wrapperClassName,
    innerClassName,
    size = "sm",
    isLoading = false,
    variant = "gold",
    borderColor,
    contentColor,
    tintedBackground,
    disabled,
    style,
    ...props
  },
  ref,
) {
  const preset = variantPreset[variant];
  const useTint =
    tintedBackground ?? (variant === "blue" || variant === "success");

  const innerStyle: CSSProperties = {
    ...(contentColor ? { color: contentColor } : {}),
    ...(borderColor
      ? {
          borderColor,
          ...(useTint ? { backgroundColor: `${borderColor}14` } : {}),
        }
      : {}),
    ...style,
  };

  return (
    <button
      ref={ref}
      type="button"
      disabled={disabled || isLoading}
      className={cn(
        "inline-flex items-center justify-center rounded-xl",
        "transition-all duration-200",
        "disabled:opacity-60 disabled:pointer-events-none",
        preset.inner,
        !borderColor && preset.innerBorder,
        borderColor && "border",
        useTint ? preset.innerBgTinted : preset.innerBg,
        !borderColor && preset.hover,
        sizeClasses[size],
        wrapperClassName,
        className,
        innerClassName,
      )}
      style={innerStyle}
      {...props}
    >
      {isLoading ? (
        <Loader2 className={`${OUTLINE_ICON_BUTTON_ICON_CLASS} animate-spin`} />
      ) : (
        children
      )}
    </button>
  );
});

/** @deprecated Use ColoredOutlineButton */
export const GoldOutlineButton = ColoredOutlineButton;
